import { useEffect } from "react";

import { AnalyticsEvent, useAnalyticsLogger, useUnauthUrlFields } from "@shared/hooks";
import { useLocation } from "@shared/react-router-dom";
// eslint-disable-next-line @fullscript/cross-reference
import type { PatientAttributes } from "@unauthenticated/signupSignin/data/types";
import { WellevateAuthorizeStatus } from "@unauthenticated/wellevateAuthorize/patients/types";

import { AuthorizeFailExpiredToken, AuthorizeFailNotEligible } from "../result";
import { WellevateEmailCapture, WellevateStepSignIn, WellevateStepSignUp } from "../steps";

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  ssoErrors?: string;
  isNewPatient: boolean;
  error?: string;
  pageVersion: string;
  isEmailCapture?: boolean;
}

const WellevateAuthorizeFlow = ({
  patientAttributes,
  additionalAttributes,
  ssoErrors,
  isNewPatient,
  error,
  pageVersion,
  isEmailCapture,
}: Props) => {
  const { pathname } = useLocation();
  const logAnalyticsEvent = useAnalyticsLogger();
  const unauthUrlFields = useUnauthUrlFields();

  const getInitialStatus = () => {
    switch (true) {
      case isNewPatient && !!error:
        return WellevateAuthorizeStatus.CHECK_INBOX;
      case isNewPatient:
        return WellevateAuthorizeStatus.SIGN_UP;
      case error === "Ineligible":
        return WellevateAuthorizeStatus.NOT_ELIGIBLE;
      case isEmailCapture || error === "Invalid Token":
        return WellevateAuthorizeStatus.EMAIL_CAPTURE;
      default:
        return WellevateAuthorizeStatus.SIGN_IN;
    }
  };

  const status = getInitialStatus();

  useEffect(() => {
    logAnalyticsEvent({
      eventName: AnalyticsEvent.UNAUTH_PAGE_VIEW,
      payload: {
        ...unauthUrlFields,
        url: pathname,
        form_version: status.toString(),
        token_status: error ?? (isEmailCapture ? "" : "Valid"),
      },
    });
  }, [status]);

  const renderContent = () => {
    switch (status) {
      case WellevateAuthorizeStatus.CHECK_INBOX:
        return <AuthorizeFailExpiredToken patientEmail={patientAttributes.email} />;
      case WellevateAuthorizeStatus.SIGN_UP:
        return (
          <WellevateStepSignUp patientAttributes={patientAttributes} pageVersion={pageVersion} />
        );
      case WellevateAuthorizeStatus.NOT_ELIGIBLE:
        return <AuthorizeFailNotEligible />;
      case WellevateAuthorizeStatus.EMAIL_CAPTURE:
        return <WellevateEmailCapture />;
      default:
        return (
          <WellevateStepSignIn
            patientAttributes={patientAttributes}
            additionalAttributes={additionalAttributes}
            ssoErrors={ssoErrors}
          />
        );
    }
  };

  return <>{renderContent()} </>;
};

export { WellevateAuthorizeFlow };
